import { sendToBusinessCentral } from "../utils/businessCentralUtils";
import { esIdTpvReconciliable } from "../utils/idTpv";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("factura");

// La acción es **no vinculada**: vive en la raíz del servicio, no bajo
// `Company('…')`. Llamarla en la ruta de compañía devuelve 404 --comprobado--,
// por eso `sendToBusinessCentral` va con `isCustomEndpoint` en true.
const ACCION = "Importaciones_estadoFactura";

/**
 * Le pregunta a Business Central qué sabe de una factura antes de reanudar su
 * envío.
 *
 * Hasta ahora el terminal reanudaba por su propio `syncStage`: si decía
 * «cabecera enviada», mandaba las líneas. Pero ese estado es local y puede
 * mentir --si la respuesta se perdió, la cabecera pudo crearse igual-- y sobre
 * todo no sabe si en BC hay **más de un** documento con el mismo identificador.
 * Reanudar sobre uno de dos duplicados escribe líneas en el equivocado.
 *
 * Acuerdo 11 (§3.5): consumir `estadoFactura` y no reanudar a ciegas si
 * `coincidencias > 1`.
 *
 * La respuesta llega como texto dentro de `value` y siempre empieza por `{`
 * --nunca por `Error:`--, así que se parsea acá y el llamador recibe el objeto.
 *
 * @returns {Promise<Object|null>} El estado, o `null` si no se pudo consultar.
 *   `null` significa «no sé», no «no existe»: el llamador no debe tomarlo como
 *   permiso para nada.
 */
export const consultarEstadoFactura = async ({ tienda, caja, idTpv, no, documentType }) => {
  // Dos formas de preguntar, y basta con una.
  //
  // La de siempre es la terna con el `Id_TPV`, que sirve para reanudar un envío.
  // La otra es el **número del documento registrado**, y hace falta porque un
  // documento traído de históricos no tiene `Id_TPV` --`mapPostedInvoice` no lo
  // mapea-- y aun así se puede reimprimir. Sin esta segunda vía, reimprimir un
  // tiquete viejo dejaba la clave en blanco para siempre.
  const numero = String(no ?? "").trim();
  if (!numero && !esIdTpvReconciliable(idTpv)) return null;

  try {
    const respuesta = await sendToBusinessCentral(
      ACCION,
      {
        data: JSON.stringify({
          tienda: tienda || "",
          caja: caja || "",
          idTpv: numero ? "" : idTpv,
          no: numero,
          documentType: documentType || "Invoice",
        }),
      },
      true
    );

    if (respuesta?.error) {
      registro.warn(`Business Central rechazó la consulta de estado: ${respuesta.error}`);
      return null;
    }

    const texto = respuesta?.value;
    if (typeof texto !== "string" || !texto.trim().startsWith("{")) {
      registro.warn("La consulta de estado no devolvió un objeto");
      return null;
    }

    return JSON.parse(texto);
  } catch (error) {
    // No bloquea el envío: si no se pudo preguntar, el terminal queda como
    // estaba antes de que esta consulta existiera. Lo que sí bloquea es una
    // respuesta que diga que hay duplicados, y eso lo decide el llamador.
    registro.warn(`No se pudo consultar el estado en Business Central: ${error.message}`);
    return null;
  }
};

/**
 * Si Business Central tiene más de un documento con este identificador.
 *
 * Sólo devuelve `true` cuando la respuesta lo afirma. Un `null` --no se pudo
 * preguntar-- no es un duplicado: es no saber, y frenar cada envío porque BC no
 * contestó sería peor que el problema que se quiere evitar.
 */
export const hayDuplicadosEnBC = (estado) => Number(estado?.coincidencias) > 1;
